import React, { useState, useEffect } from 'react';
import { X, Cpu, FileText, CheckCircle2 } from 'lucide-react';

export const ENGINE_OPTIONS = [
  { value: "standard_layout",   label: "Standard",    shortDesc: "Fast extraction for clean, typed digital documents" },
  { value: "advanced_vision",   label: "Smart Scan",  shortDesc: "Reads scanned docs, handwriting & forms — outputs structured Markdown" },
  { value: "form_intelligence", label: "Deep Scan",   shortDesc: "Preserves exact document layout — best for long, complex documents" },
  { value: "contextual_ai",     label: "AI Vision",   shortDesc: "AI reads the document directly — no text extraction step" },
];

// ── Static engine detail content ────────────────────────────────────────────
const ENGINE_DETAILS = {
  standard_layout: {
    recommended: true,
    outputFormat: "Layout Text",
    speed: "Fastest",
    speedDots: 5,
    accuracyDots: 3,
    whenToUse: "PDFs created on a computer — invoices from accounting software, bank statements, digital contracts you received by email. Any document where you can select and copy-paste text.",
    skipWhen: "The document is scanned from paper, photographed, or contains handwritten text.",
    samples: [
      { label: "Digital Invoice" },
      { label: "Bank Statement" },
      { label: "Export PDF" },
    ],
  },
  advanced_vision: {
    recommended: false,
    outputFormat: "Markdown",
    speed: "Medium",
    speedDots: 3,
    accuracyDots: 5,
    whenToUse: "Scanned paper documents, photographed docs, handwritten text in any language (Hindi, English, mixed). Government forms, medical records, structured forms with checkboxes, images, or stamps.",
    skipWhen: "Long multi-page invoices (10+ pages) — Markdown output can confuse the AI. Simple digital PDFs — Standard is faster and free.",
    note: "Best handwriting recognition across languages",
    samples: [
      { label: "Scanned Invoice" },
      { label: "Handwritten Form" },
      { label: "Gov. Form" },
    ],
  },
  form_intelligence: {
    recommended: false,
    outputFormat: "Layout Text",
    speed: "Medium",
    speedDots: 3,
    accuracyDots: 4,
    whenToUse: "Long invoices (10+ pages) where line items span multiple pages. Complex multi-column layouts, purchase orders with detailed tables. Any document where spatial layout matters for understanding.",
    skipWhen: "You need to read handwritten text — Smart Scan is better for that. Simple short digital PDFs — Standard is faster.",
    samples: [
      { label: "Long Invoice" },
      { label: "Multi-page PO" },
      { label: "Complex Layout" },
    ],
  },
  contextual_ai: {
    recommended: false,
    outputFormat: "Direct AI Vision",
    speed: "Slowest",
    speedDots: 1,
    accuracyDots: 4,
    whenToUse: "Short documents (under 30 pages) where the AI needs to \"see\" everything. Visually rich documents with charts, diagrams, or mixed media. Documents where OCR might miss visual cues.",
    skipWhen: "Documents over 30 pages — quality drops significantly, cross-page context is lost. Cost-sensitive processing — this is the most expensive engine.",
    warning: "Max 1,000 pages / 50MB per file. Reliable quality only under ~30 pages.",
    samples: [
      { label: "Short Contract" },
      { label: "Visual Report" },
      { label: "Diagram Doc" },
    ],
  },
};

// ── Sample PDF links — replace with real URLs when available ────────────────
// Format: { [engine_value]: [url1, url2, url3] }
const SAMPLE_PDF_URLS = {
  standard_layout:   [null, null, null],
  advanced_vision:   [null, null, null],
  form_intelligence: [null, null, null],
  contextual_ai:     [null, null, null],
};

// ── Dot indicator ────────────────────────────────────────────────────────────
const Dots = ({ filled, total = 5 }) => (
  <div className="flex gap-[3px] mt-1">
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i < filled ? "bg-[#10b981]" : "bg-black/10"}`} />
    ))}
  </div>
);

// ── Single engine card ───────────────────────────────────────────────────────
const EngineCard = ({ opt, detail, selected, onClick, sampleUrls }) => (
  <button
    className={`relative text-left p-4 rounded-xl border flex flex-col items-start transition-all duration-200 cursor-pointer outline-none hover:border-[#8B55DD]/30 hover:bg-[#F5F3FF] ${selected ? "border-[#8B55DD] bg-[#F5F3FF] shadow-[0_0_0_1px_#8B55DD]" : "border-black/10 bg-[#FAFAFA]"}`}
    onClick={() => onClick(opt.value)}
    type="button"
  >
    {detail.recommended && <span className="absolute top-0 right-0 bg-[#8B55DD] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-bl-lg rounded-tr-[11px]">Recommended</span>}

    <div className="text-base font-bold text-zinc-800 m-0">{opt.label}</div>
    <span className="inline-block mt-1 font-mono text-[10px] bg-[#E9D5FF] text-[#8B55DD] px-1.5 py-0.5 rounded">Output: {detail.outputFormat}</span>
    <div className="text-xs text-zinc-500 mt-2 leading-relaxed min-h-[2.5rem] p-0 m-0">{opt.shortDesc}</div>

    <div className="w-full flex gap-4 mt-4 bg-white border border-black/5 rounded-lg p-2.5">
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Speed</span>
        <span className="text-xs font-semibold text-zinc-700">{detail.speed}</span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Accuracy</span>
        <Dots filled={detail.accuracyDots} />
      </div>
    </div>

    <div className="w-full h-px bg-black/5 my-4" />

    <div>
      <div className="text-[11px] font-bold text-zinc-700 uppercase tracking-wider mb-1">When to use</div>
      <div className="text-xs text-zinc-500 leading-relaxed">{detail.whenToUse}</div>
    </div>

    <div className="mt-4">
      <div className="text-[11px] font-bold text-zinc-700 uppercase tracking-wider mb-1" style={{ color: "#f59e0b" }}>Skip when</div>
      <div className="text-xs text-zinc-500 leading-relaxed">{detail.skipWhen}</div>
    </div>

    {detail.note && (
      <div style={{ fontSize: "0.625rem", color: "#10b981", fontWeight: 600, marginTop: "0.375rem" }}>
        ★ {detail.note}
      </div>
    )}

    {detail.warning && (
      <div style={{ fontSize: "0.625rem", color: "#f59e0b", fontWeight: 600, marginTop: "0.375rem" }}>
        ⚠ {detail.warning}
      </div>
    )}

    {/* Sample PDF thumbnails */}
    <div className="w-full h-px bg-black/5 my-4" />
    <div className="text-[11px] font-bold text-zinc-700 uppercase tracking-wider mb-1">Sample documents</div>
    <div className="w-full flex gap-2 overflow-x-auto pb-1 mt-2 mb-[-4px]">
      {detail.samples.map((s, i) => {
        const url = sampleUrls?.[i];
        return (
          <a
            key={i}
            className="flex items-center gap-1.5 px-2 py-1.5 bg-white border border-black/10 rounded-md text-[10px] font-medium text-zinc-600 no-underline whitespace-nowrap hover:border-[#8B55DD]/30 hover:text-[#8B55DD] transition-colors"
            href={url || undefined}
            target="_blank"
            rel="noopener noreferrer"
            title={url ? `Open: ${s.label}` : "Sample PDF — coming soon"}
            onClick={(e) => {
              e.stopPropagation();
              if (!url) e.preventDefault();
            }}
            style={{ opacity: url ? 1 : 0.5 }}
          >
            <FileText size={14} color="#9ca3af" />
            <span className="whitespace-nowrap">{s.label}</span>
          </a>
        );
      })}
    </div>
  </button>
);

// ── Reusable engine card grid (used both in modal and wizard) ────────────────
export const EngineCardGrid = ({ selected, onChange }) => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4 w-full p-4">
    {ENGINE_OPTIONS.map((opt) => (
      <EngineCard
        key={opt.value}
        opt={opt}
        detail={ENGINE_DETAILS[opt.value]}
        selected={selected === opt.value}
        onClick={onChange}
        sampleUrls={SAMPLE_PDF_URLS[opt.value]}
      />
    ))}
  </div>
);

// ── Main modal ───────────────────────────────────────────────────────────────
const OcrEngineModal = ({ isOpen, onClose, onSelect, currentEngine }) => {
  const [selected, setSelected] = useState(null);

  // Reset selection each time modal opens — force user to actively choose
  useEffect(() => {
    if (isOpen) {
      setSelected(currentEngine || null);
    }
  }, [isOpen, currentEngine]);

  if (!isOpen) return null;

  const canConfirm = !!selected;

  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm flex items-center justify-center z-[1000] p-6 animate-[ex-fade-in_0.2s_ease]">
      <div className="bg-white border border-black/10 rounded-2xl w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden shadow-[0_0_0_1px_rgba(139,85,221,0.04),0_24px_64px_rgba(0,0,0,0.12),0_8px_24px_rgba(0,0,0,0.08)] animate-[ex-slide-up_0.25s_ease]">
        {/* Header */}
        <div className="px-6 py-[18px] bg-gradient-to-b from-[#FAFAFA] to-white border-b border-black/5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#8B55DD]/15 to-[#8B55DD]/5 border border-[#8B55DD]/15 flex items-center justify-center text-[#8B55DD]"><Cpu size={16} /></div>
            <div className="flex flex-col">
              <h2 className="text-[15px] p-0 font-bold text-zinc-800 leading-snug m-0">Choose Extraction Engine</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5 m-0">Select the engine that best matches your documents. Click a card to learn more and choose.</p>
            </div>
          </div>
          <button className="w-7 h-7 rounded-md border border-black/10 bg-[#F5F4F3] text-zinc-500 flex items-center justify-center cursor-pointer transition-colors hover:text-red-600 hover:border-red-600/20 hover:bg-red-600/5 p-0" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Engine card grid */}
        <div className="flex-1 overflow-y-auto p-0 flex flex-col custom-scrollbar">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4 w-full p-4">
            {ENGINE_OPTIONS.map((opt) => (
              <EngineCard
                key={opt.value}
                opt={opt}
                detail={ENGINE_DETAILS[opt.value]}
                selected={selected === opt.value}
                onClick={setSelected}
                sampleUrls={SAMPLE_PDF_URLS[opt.value]}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#FAFAFA] border-t border-black/5 flex items-center justify-end gap-3 shrink-0">
          <button className="bg-transparent text-zinc-500 border border-black/10 hover:bg-[#F5F4F3] hover:text-zinc-800 hover:border-black/15 inline-flex items-center gap-1.5 px-3 py-[7px] rounded-lg text-[13px] font-semibold cursor-pointer transition-all duration-150" onClick={onClose}>Cancel</button>
          <button
            className="bg-gradient-to-r from-[#8B55DD] to-[#9177B8] text-white shadow-sm hover:from-[#7A48CC] hover:to-[#8068A8] hover:shadow-md disabled:opacity-35 disabled:cursor-not-allowed inline-flex items-center gap-1.5 px-3 py-[7px] border-none rounded-lg text-[13px] font-semibold cursor-pointer transition-all duration-150"
            disabled={!canConfirm}
            onClick={() => { onSelect(selected); onClose(); }}
          >
            <CheckCircle2 size={14} />
            {canConfirm
              ? `Use ${ENGINE_OPTIONS.find(o => o.value === selected)?.label}`
              : "Select an engine above"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OcrEngineModal;
